export type SafeRedirectLocation = {
  pathname: string;
  search?: string;
  hash?: string;
};

const fallbackOrigin = "http://localhost";

function getOrigin() {
  return typeof window === "undefined"
    ? fallbackOrigin
    : window.location.origin;
}

function isUnsafeRedirectValue(value: string) {
  let decodedValue = value;

  try {
    decodedValue = decodeURIComponent(value);
  } catch {
    return true;
  }

  const hasControlCharacter = [...decodedValue].some((char) => {
    const code = char.charCodeAt(0);

    return code <= 31 || code === 127;
  });

  return (
    value === "" ||
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value.includes("\\") ||
    decodedValue.startsWith("//") ||
    decodedValue.includes("\\") ||
    hasControlCharacter ||
    /^[a-z][a-z0-9+.-]*:/i.test(value)
  );
}

export function getSafeRedirectLocation(
  value: string | null | undefined,
  fallback = "/dashboard",
): SafeRedirectLocation {
  const candidate = (value || "").trim();
  const safeFallback = isUnsafeRedirectValue(fallback) ? "/" : fallback;

  if (isUnsafeRedirectValue(candidate)) {
    return getSafeRedirectLocation(safeFallback, "/");
  }

  try {
    const url = new URL(candidate, getOrigin());

    if (url.origin !== getOrigin()) {
      return getSafeRedirectLocation(safeFallback, "/");
    }

    return {
      pathname: url.pathname || "/",
      search: url.search || undefined,
      hash: url.hash || undefined,
    };
  } catch {
    return getSafeRedirectLocation(safeFallback, "/");
  }
}

export function getSafeRedirectPath(
  value: string | null | undefined,
  fallback = "/dashboard",
) {
  const redirect = getSafeRedirectLocation(value, fallback);

  return `${redirect.pathname}${redirect.search || ""}${redirect.hash || ""}`;
}
